import React, { useEffect, useState, useRef } from 'react';
import styles from './ScrollToTopButton.module.scss';
import HdIcon from '../../../../media/components/icon/HdIcon';
import { HdIconButton } from '../../../../components';

interface ScrollToTopButtonProps {
  scrollViewportId: string;
  variant?: 'default' | 'compact';
  className?: string;
}

export default function ScrollToTopButton({
  scrollViewportId,
  variant = 'default',
  className = ''
}: ScrollToTopButtonProps) {
  const [scrollViewport, setScrollViewport] = useState<HTMLElement>(null);
  const scrollToTopButtonRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    if (scrollViewport) {
      scrollViewport.scrollTo({ top: 0 });
    }
  };

  useEffect(() => {
    const viewport = document.getElementById(scrollViewportId);

    if (viewport) {
      setScrollViewport(viewport);
    }
  }, []);

  // Floats over the viewport once half of it has scrolled by; it reserves no room under the rows.
  useEffect(() => {
    if (!scrollViewport) {
      return undefined;
    }

    const parent = scrollViewport.parentElement;
    const parentPosition = parent.style.position;

    const syncToScrollPosition = () => {
      const pastThreshold = scrollViewport.scrollTop > scrollViewport.clientHeight / 2;

      scrollToTopButtonRef.current?.classList.toggle(styles.hidden, !pastThreshold);
      parent.style.position = pastThreshold ? 'relative' : parentPosition;
    };

    scrollViewport.addEventListener('scroll', syncToScrollPosition);

    return () => {
      scrollViewport.removeEventListener('scroll', syncToScrollPosition);
      parent.style.position = parentPosition;
    };
  }, [scrollViewport]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {scrollViewport ? (
        <div
          ref={scrollToTopButtonRef}
          className={`${styles.scrollToTopButtonWrapper} ${styles.hidden} ${className} `}
          data-id={`${scrollViewportId}-scroll-top-wrapper`}
        >
          <HdIconButton
            className={styles.scrollToTopButton}
            onClick={scrollToTop}
            dataId={`${scrollViewportId}-scroll-top`}
          >
            <HdIcon size={variant === 'compact' ? 2 : 3} className={styles.icon} name='up-arrow' />
          </HdIconButton>
        </div>
      ) : null}
    </>
  );
}
